import React, { useEffect } from "react";
import { RydProject } from "../../../model/project";
import { RydViewport } from "../RydViewport";
import {
    getLeftNearestGridPoint,
    getRenderedWidthForHNC,
    normalizeClassName,
    NOTE_WIDTH,
    positionToTick,
} from "../../../render/renderUtils";
import { CaretController } from "../../../utils/ScrollController";
import { useZoomStore } from "../../../stores/ZoomStore";
import { drawTimelineHeader } from "../../../render/canvasRenderer";
import {
    useWorkspaceBookmarks,
    useWorkspaceUIState
} from "../../../stores/WorkspaceStore";
import { RydBookmarksContainer } from "./RydBookmarksContainer";
import { RydTimelineContextMenu } from "../../dialogs/contextMenus/RydTimelineContextMenu";
import {
    useSetTimelineSelection,
    useTimelineSelection,
} from "../../../stores/TimelineSelectionStore";
import { computeTimelineSelectionStyle } from "../../../utils/ui/selection";


let isShiftPressed = false;
export type TimelineEventSource = {
    target: EventTarget & HTMLElement,
    normalizedClickPosition: number,
    timelinePosition: number,
    selection?: { start: number, end: number }
};

export type RydTimelineSelection = {
    start: number,
    end: number
};

export function RydTimelineHeader({
    project,
    caretController
}: {
    project: RydProject,
    caretController?: CaretController
}) {
    const wrappingDivRef = React.useRef<HTMLDivElement>(null);
    const [contextMenuTarget, setContextMenuTarget] = React.useState<TimelineEventSource>();
    const [canvasWidth, setCanvasWidth] = React.useState<number>();
    const zoomStore = useZoomStore();
    const bookmarks = useWorkspaceBookmarks();
    const workspaceTimelineSelection = useTimelineSelection();
    const workspaceSetTimelineSelection = useSetTimelineSelection();
    const workspaceUiState = useWorkspaceUIState();


    useEffect(() => {
        setCanvasWidth(wrappingDivRef.current?.parentElement?.getBoundingClientRect().width || 800);
    }, [wrappingDivRef.current]);

    const [offset, setOffset] = React.useState(0);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const startXRef = React.useRef<number>(0);
    const endXRef = React.useRef<number>(0);

    useEffect(() => {
        if (!caretController?.scrollController) return;
        const listenerId = caretController?.scrollController.listen(setOffset);
        return () => caretController?.scrollController?.drop(listenerId);
    }, [project, caretController]);

    useEffect(() => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) return;

        drawTimelineHeader(context, canvas, project, canvas.width, zoomStore.zoomFactor, offset);
    }, [project, zoomStore.zoomFactor, offset]);

    const checkShiftDown = (e) => {
        if (e.key === 'Shift') {
            isShiftPressed = true;
        }
    };

    const checkShiftUp = (e) => {
        if (e.key === 'Shift') {
            isShiftPressed = false;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', checkShiftDown);
        document.addEventListener('keyup', checkShiftUp);
        return () => {
            document.removeEventListener('keydown', checkShiftDown);
            document.removeEventListener('keyup', checkShiftUp);
        }
    }, []);

    // Mouse event handlers for dragging/zooming the timeline
    const handleMouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        startXRef.current = event.clientX - event.currentTarget.getBoundingClientRect().left;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const updateZoom = (deltaX: number) => {
        const newValue = zoomStore.zoomFactor + zoomStore.zoomFactor * (deltaX / 10) / 100;
        zoomStore.setZoomFactor(newValue);
    };

    const createLoop = (region: { start: number, end: number }) => {
        workspaceSetTimelineSelection(region);
    }

    const handleMouseMove = (event: MouseEvent) => {
        if (isShiftPressed) {
            updateZoom(startXRef.current - event.clientX);
        } else {
            endXRef.current = event.clientX - (event.target as HTMLElement).getBoundingClientRect().left;
        }
    };

    const handleMouseUp = (e: MouseEvent) => {
        if (e.button === 2) return; // right click: allow menu to open
        if (contextMenuTarget) return; // we already opened the context menu

        const selection: RydTimelineSelection = {
            start: positionToTick(NOTE_WIDTH, zoomStore.zoomFactor, startXRef.current + offset),
            end: positionToTick(NOTE_WIDTH, zoomStore.zoomFactor, endXRef.current + offset) - 1
        };
        if (workspaceUiState.snapToGrid) {
            // FIXME the project should have a global ppq value
            const ppq = project.getTrack(0)?.sequence.getPPQ() || 480;
            selection.start = getLeftNearestGridPoint(
                selection.start,
                ppq,
                zoomStore.zoomFactor,
                workspaceUiState.gridResolution,
                undefined
            );
            // FIXME: improve the snap to grid: currently it forces the user to have the end of the
            // region after (i.e. to the left) the desired region's end. it would be nicer if it would
            // decide on the region respecting the grid resolution and the proximity on its boundaries.
            selection.end = getLeftNearestGridPoint(
                selection.end,
                ppq,
                zoomStore.zoomFactor,
                workspaceUiState.gridResolution,
                undefined
            );
        }

        if (selection.end != 0 && selection.end > selection.start) {
            // this seem to be an intentional selection
            createLoop(selection);
        } else {
            setContextMenuTarget(undefined);
            workspaceSetTimelineSelection(undefined);
        }
        startXRef.current = 0;
        endXRef.current = 0;

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleRightClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        const normalizedClickPosition = event.clientX - event.currentTarget.getBoundingClientRect().left;
        setContextMenuTarget({
            target: event.target as HTMLElement,
            normalizedClickPosition,
            timelinePosition: positionToTick(NOTE_WIDTH, zoomStore.zoomFactor, normalizedClickPosition + offset),
            selection: workspaceTimelineSelection
                ? { start: workspaceTimelineSelection.start, end: workspaceTimelineSelection.end }
                : undefined
        });
    };

    const workspaceTimelineSelectionStyle: React.CSSProperties = workspaceTimelineSelection
        ? {
            top: '8px',
            height: '18px',
            ...computeTimelineSelectionStyle(zoomStore.zoomFactor, workspaceTimelineSelection, offset)
        }
        : {};

    return (
        <RydViewport
            name="timeline-header"
            sticky={<div className="piano-roll-header-info-bar"
                style={{
                    height: 'var(--piano-roll-header-height)'
                }}>

                <div className={normalizeClassName(["piano-roll-header-controls", 'header-controls'])}
                    style={{
                        height: '16px',
                        backgroundColor: 'var(--timeline-header-background)'
                    }}>

                </div>
            </div>}
            hideTitle={true}
            adaptiveHeight={true}
            scrollController={caretController?.scrollController}
        >
            <>
                <div
                    className="piano-roll-content-wrapper"
                    ref={wrappingDivRef}
                    style={{
                        display: 'inline-flex',
                        position: 'relative',
                        width: getRenderedWidthForHNC({ length: project.getLength(), includeKeyboard: true }),
                        height: 'calc(18px + 8px)',
                    }}
                >
                    <RydBookmarksContainer
                        project={project}
                        bookmarks={bookmarks}
                        zoomFactor={zoomStore.zoomFactor}
                        canvasWidth={canvasWidth || 0}
                        showLoops={true}
                    />

                    {canvasWidth && <canvas
                        onMouseDown={handleMouseDown}
                        onContextMenu={handleRightClick}
                        ref={canvasRef}
                        width={canvasWidth}
                        height={18}
                        style={{
                            position: 'absolute',
                            // +1px comes from .piano-roll-header.piano-roll-header--pianoroll that defines
                            // a 1px red border. This +1 compensates for that.
                            left: `calc(var(--piano-roll-header-width) + ${offset}px + 1px)`,
                            top: '8px',
                            height: '18px',
                            width: `${canvasWidth}px`,
                        }}
                    />}
                    {workspaceTimelineSelection && <div
                        className="timeline-selection-overlay"
                        style={workspaceTimelineSelectionStyle}
                    />}
                </div>
                {contextMenuTarget &&
                    <RydTimelineContextMenu
                        target={contextMenuTarget}
                        project={project}
                        zoomFactor={zoomStore.zoomFactor}
                        offset={offset}
                        closeCB={() => setContextMenuTarget(undefined)}
                    />
                }
            </>
        </RydViewport>
    );
}
